import {
  ActivityType,
  ActivityStepType,
  ActivityUserSelectInstruction,
  ActivityStepLayout,
  ActivityStepAudio,
  ActivityStepVoter,
  ActivityStepScoreStyle,
  ActivityGroupSettingsAiDifficulty,
} from '@/types'

export const AI_DIFFICULTY_TO_DISPLAY_NAME: Record<
  ActivityGroupSettingsAiDifficulty,
  string
> = {
  [ActivityGroupSettingsAiDifficulty.ASCENDING]: 'Ascending',
  [ActivityGroupSettingsAiDifficulty.EASY]: 'Easy',
  [ActivityGroupSettingsAiDifficulty.MEDIUM]: 'Medium',
  [ActivityGroupSettingsAiDifficulty.HARD]: 'Hard',
}

export const ACTIVITY_TYPE_TO_DISPLAY_NAME: Record<ActivityType, string> = {
  [ActivityType.Default]: 'Default',
  [ActivityType.SpiralDrawing]: 'Avatar Drawing',
  [ActivityType.Finale]: 'Finale',
  [ActivityType.Craps]: 'Craps',
  [ActivityType.MagicNumber]: 'Magic Number',
  [ActivityType.Roulette]: 'Roulette',
  [ActivityType.TextEntry]: 'Text Entry',
  [ActivityType.Freeform]: 'Freeform',
  [ActivityType.TeamName]: 'Team Name',
  [ActivityType.Categories]: 'Categories',
  [ActivityType.DiscussionGroup]: 'Discussion Group',
  [ActivityType.QuestionCards]: 'Question Cards',
  [ActivityType.Charades]: 'Charades',
  [ActivityType.FamilyFeud]: 'Family Feud',
  [ActivityType.MultipleChoice]: 'Multiple Choice',
  [ActivityType.Giphy]: 'Giphy',
  [ActivityType.WordCloud]: 'Word Cloud',
  [ActivityType.RatingPoll]: 'Rating Poll',
  [ActivityType.BuzzIn]: 'Buzz In',
  [ActivityType.Lipdub]: 'Lipdub',
  [ActivityType.DrawingEachTeam]: 'Drawing Each Team',
  [ActivityType.DrawingPictionary]: 'Drawing Pictionary',
  [ActivityType.PhotoSequences]: 'Photo Sequences',
  [ActivityType.TakePhoto]: 'Take Photo',
  [ActivityType.TeamPhoto]: 'Team Photo',
  [ActivityType.PhotoBooth]: 'Photo Booth',
  [ActivityType.VideoIndividual]: 'Video Individual',
  [ActivityType.VideoTeam]: 'Video Team',
  [ActivityType.Match]: 'Match Game',
  [ActivityType.FactMatch]: 'Fact Match',
  [ActivityType.OrderTheCards]: 'Order The Cards',
  [ActivityType.RoyalRumble]: 'Royal Rumble',
  [ActivityType.WhiteElephant]: 'White Elephant',
  [ActivityType.Poll]: 'Poll',
  [ActivityType.Award]: 'Awards',
  [ActivityType.Slides]: 'Slides',
  [ActivityType.TelephonePictionary]: 'Telephone Pictionary',
  [ActivityType.PriceIsRight]: 'Price is Right',
  [ActivityType.RaiseTheStakes]: 'Raise the Stakes',
  [ActivityType.TwoTruths]: 'Two Truths',
  [ActivityType.TwoTruthsReveal]: 'Two Truths Reveal',
  [ActivityType.YouTube]: 'YouTube',
  [ActivityType.HandMovement]: 'Hand Movement',
  [ActivityType.UnconditionalPositiveRegard]: 'Unconditional Positive Regard',
  [ActivityType.PlayerRating]: 'Player Rating',
  [ActivityType.StartButton]: 'Start Button',
  [ActivityType.Raffle]: 'Raffle',
  [ActivityType.QRCode]: 'QR Code',
  [ActivityType.JeopardAI]: 'JeopardAI',
  [ActivityType.MusicalKeys]: 'Musical Keys',
  [ActivityType.HeadToHead]: 'Head to Head',
  [ActivityType.Evaluate]: 'Evaluate',
}

export const ACTIVITY_STEP_TYPE_TO_DISPLAY_NAME: Record<
  ActivityStepType,
  string
> = {
  [ActivityStepType.Speaker]: 'Speaker',
  [ActivityStepType.Display]: 'Display',
  [ActivityStepType.Play]: 'Play',
  [ActivityStepType.Vote]: 'Vote',
  [ActivityStepType.Result]: 'Result',
}

export const ACTIVITY_USER_SELECT_INSTRUCTION_TO_DISPLAY_NAME: Record<
  ActivityUserSelectInstruction,
  string
> = {
  [ActivityUserSelectInstruction.OnePerTeam]: 'One Per Team',
  [ActivityUserSelectInstruction.OnePerRoom]: 'One Per Room',
  [ActivityUserSelectInstruction.All]: 'All',
  [ActivityUserSelectInstruction.None]: 'None',
}

export const ACTIVITY_STEP_LAYOUT_TO_DISPLAY_NAME: Record<
  ActivityStepLayout,
  string
> = {
  [ActivityStepLayout.Theater]: 'Theater',
  [ActivityStepLayout.Default]: 'Default',
  [ActivityStepLayout.Social]: 'Social',
  [ActivityStepLayout.Highlight]: 'Highlight',
  [ActivityStepLayout.Meeting]: 'Meeting',
  [ActivityStepLayout.Huddle]: 'Huddle',
}

export const ACTIVITY_STEP_AUDIO_TO_DISPLAY_NAME: Record<
  ActivityStepAudio,
  string
> = {
  [ActivityStepAudio.UserCanHearEveryone]: 'Everyone',
  [ActivityStepAudio.UserCanHearHostOnly]: 'Host Only',
  [ActivityStepAudio.UserCanHearTemmates]: 'Team Talk',
  [ActivityStepAudio.Auto]: 'Auto',
}

export const ACTIVITY_STEP_VOTER_TO_DISPLAY_NAME: Record<
  ActivityStepVoter,
  string
> = {
  [ActivityStepVoter.Everyone]: 'Everyone',
  [ActivityStepVoter.Team]: 'Team',
  [ActivityStepVoter.Host]: 'Host',
}

export const ACTIVITY_STEP_SCORE_STYLE_TO_DISPLAY_NAME: Record<
  ActivityStepScoreStyle,
  string
> = {
  [ActivityStepScoreStyle.Activity]: 'Activity',
}

export const DEFAULT_ACTIVITY_POINTS: Partial<Record<ActivityType, number>> = {
  [ActivityType.Award]: 100,
  [ActivityType.BuzzIn]: 100,
  [ActivityType.Categories]: 20,
  [ActivityType.DiscussionGroup]: 0,
  [ActivityType.DrawingEachTeam]: 150,
  [ActivityType.DrawingPictionary]: 150,
  [ActivityType.FactMatch]: 100,
  [ActivityType.Freeform]: 10,
  [ActivityType.Giphy]: 100,
  [ActivityType.HandMovement]: 100,
  [ActivityType.Match]: 100,
  [ActivityType.MultipleChoice]: 60,
  [ActivityType.OrderTheCards]: 100,
  [ActivityType.PhotoBooth]: 500,
  [ActivityType.PriceIsRight]: 100,
  [ActivityType.RaiseTheStakes]: 100,
  [ActivityType.TakePhoto]: 1000,
  [ActivityType.TeamName]: 50,
  [ActivityType.TelephonePictionary]: 100,
  [ActivityType.TextEntry]: 60,
  [ActivityType.TwoTruths]: 10,
  [ActivityType.TwoTruthsReveal]: 100,
  [ActivityType.VideoIndividual]: 100,
  [ActivityType.VideoTeam]: 500,
  [ActivityType.WhiteElephant]: 10,
  [ActivityType.MusicalKeys]: 400,
}

export const DEFAULT_ACTIVITY_USER_SELECTION: Partial<
  Record<ActivityType, ActivityUserSelectInstruction>
> = {
  [ActivityType.Freeform]: ActivityUserSelectInstruction.All,
  [ActivityType.Categories]: ActivityUserSelectInstruction.All,
  [ActivityType.Poll]: ActivityUserSelectInstruction.All,
  [ActivityType.PhotoBooth]: ActivityUserSelectInstruction.All,
  [ActivityType.Award]: ActivityUserSelectInstruction.All,
  [ActivityType.VideoIndividual]: ActivityUserSelectInstruction.All,
  [ActivityType.WhiteElephant]: ActivityUserSelectInstruction.All,
  [ActivityType.MusicalKeys]: ActivityUserSelectInstruction.All,
}

export const DEFAULT_ACTIVITY_SPEED_MATTERS: Partial<
  Record<ActivityType, boolean>
> = {
  [ActivityType.Freeform]: false,
  [ActivityType.TeamName]: false,
  [ActivityType.Giphy]: false,
  [ActivityType.BuzzIn]: false,
  [ActivityType.Categories]: false,
  [ActivityType.Poll]: false,
  [ActivityType.FactMatch]: false,
  [ActivityType.DrawingEachTeam]: false,
  [ActivityType.PhotoBooth]: false,
  [ActivityType.TwoTruths]: false,
  [ActivityType.TwoTruthsReveal]: false,
  [ActivityType.Award]: false,
  [ActivityType.VideoIndividual]: false,
  [ActivityType.VideoTeam]: false,
  [ActivityType.StartButton]: false,
  [ActivityType.WhiteElephant]: false,
  [ActivityType.HandMovement]: false,
}

export const DEFAULT_ACTIVITY_TRIES: Partial<Record<ActivityType, number>> = {
  [ActivityType.TextEntry]: 2,
  [ActivityType.Categories]: 9999,
  [ActivityType.DrawingPictionary]: 9999,
  [ActivityType.MultipleChoice]: 1,
}

export const ACTIVITY_DESCRIPTIONS: Partial<Record<ActivityType, string>> = {
  [ActivityType.Categories]:
    'a.k.a Fastest Fingers List-O-Rama. The title of a list is given (e.g. "Name the most populous cities in the world") and teams enter as many of the items on the list as quickly as possible before time runs out.',
  [ActivityType.Giphy]:
    'Gladiators, choose your GIF! Together, teams search our database of hundreds of thousands of GIFs to find the perfect one that suits the prompt. For example, "When someone says Beyonce is overrated…" Everyone votes on the GIFs to determine which is the funniest!',
  [ActivityType.Lipdub]: `We're not LL Cool J, but we welcome you to the Lip Sync Battle! The Lip-Sync block always starts with a poll where players can choose between 3 songs to lip-sync to and a 4th "Please don't make me do this" option. The option with the most votes wins. If a song is chosen, we play the song and players are cycled through along with lyric cards - points given for enthusiasm and style!`,
  [ActivityType.PhotoBooth]:
    'Make a lasting impression. Often used later in the game as a nice breather, each player has a minute or two to drag various props onto their photo to create amusing scenes for the perfect snapshot. Players can download all the photos right from the game!',
  [ActivityType.TwoTruths]:
    'How well do you know your co-workers? One player per team is selected to fill out two truths and a lie about themselves. Then they take turns reading the three "facts" and trying to trick the other teams who are guessing which is the lie.',
  [ActivityType.TwoTruthsReveal]:
    'How well do you know your co-workers? One player per team is selected to fill out two truths and a lie about themselves. Then they take turns reading the three "facts" and trying to trick the other teams who are guessing which is the lie.',
  [ActivityType.BuzzIn]:
    'Fast-paced and competitive. Each team has a representative in the middle whose job it is to buzz-in first and provide the correct answer before the other teams. Our Buzz-In round is generally trivia based but takes many different forms - Name that Tune, Finish the Lyric, Where in the World videos, Solve the Puzzle, Identify the Movie, Finish the Movie Line, etc.',
  [ActivityType.MultipleChoice]:
    'Players type short answers or select multiple-choice. The kind of trivia you know and love! Questions on just about anything for those of you who may be walking around with useless knowledge. Mind-bending riddles, puzzles, photo clues, and more.',
  [ActivityType.DrawingPictionary]: `The Classic Drawing Game. We'll warm the teams up by having them spend a few minutes creating a picture together based on a prompt. Then we play pictionary proper - one team draws and the other teams race to guess what is being drawn.`,
  [ActivityType.Award]:
    'Straighten that bowtie and take a turn on the Red Carpet. We offer up a silly award like "Most Caffeinated" or "Loudest Typer" and five random players are selected as the nominees. Everyone gets to vote before we reveal the big winner who has a moment to pose with a superimposed trophy and give a short speech to thank their parents.',
  [ActivityType.PriceIsRight]:
    'Come on down! You are the next contestant on the Price is Right! Players get to experience the thrill of being on Contestants Row. We put up an item and players confer with their team to guess the price. The closest to the actual retail price without going over wins!',
  [ActivityType.OrderTheCards]:
    'Find the match! A more tactile game, teams are tasked with dragging tiles around to match the top row with the bottom. Themes can include matching Famous Cars with the Movie they appeared in or dragging tiles around to properly unscramble a picture.',
  [ActivityType.HandMovement]:
    "This is our take on the classic carnival game where you shoot a water gun into a clown's mouth to make a horse move across the course to win. Instead of a water gun, players use their own hand movement to move the horse - very silly! Also, it's a relay race - hosts rotate scribes during the race so the whole team needs to be on their toes.",
  [ActivityType.Charades]:
    "We've taken the classic game of Charades and brought it on to our platform - and then supercharged it! In our version of Charades, all the teams go simultaneously which means there are no wasted moments. One member of each team is in the middle, gesticulating wildly in an attempt to get their teammates to type in the right answer.",
  [ActivityType.TelephonePictionary]:
    'In this mini-game, players create the first round of prompts which are then passed to other players to draw. Then the drawings are passed to still more players to describe. Those descriptions are passed to be drawn again. On and on it goes until the big reveal that shows the silly journey from original prompt to final drawing. Great for teams that love to doodle and laugh.',
  [ActivityType.RoyalRumble]:
    "Let's get ready to rumble!! This mini-game is all about engaging in that classic argument - who would win in a fight? Teams blindly pick one fighter and one weapon or power and then have to debate the other teams why their fighter would wipe the floor with the others. The twist? The imaginary combatants are usually quite silly. Who would win: a penguin in a snow fort or a koala with a selfie stick?",
  [ActivityType.Slides]:
    "In this mini-game, each team nominates one person to deliver a presentation that they've never seen and doesn't even make sense! It's all about thinking quickly on your feet and finding the humor in an absurd situation. At the end, everyone gives out points based on performance and a hearty round of applause for all.",
  [ActivityType.QuestionCards]: `Don't think - just react! In this game, players are brought into the middle one by one and are tasked with instant reactions to silly questions (e.g. "What's your egg salad recipe?") or fun directives (e.g. "Hide!"). There are no right answers but the oddest reactions get the most points!`,
  [ActivityType.FactMatch]: `Get to know your co-workers. Each player answers a question about themselves (e.g. "What would be the title of your autobiography?") and teams try to guess who said which answer. For teams that don't know each other at all, we can play this is a fact recall - players reveal what they answered and then later in the game we challenge teams to recall the answers.`,
  [ActivityType.VideoTeam]:
    'Each player is tasked with making a 6 second video based on a prompt. Then all the videos are voted on by all the players. Example: 80s dance off - record a video of your best 80s moves.',
  [ActivityType.Match]:
    'Name those celebrities! In this themed buzz-in round, players will see two celebrities mashed-up in one image. The first to buzz-in and correctly name both famous people wins!',
}

export const DEFAULT_ACTIVITY_MIN_PEOPLE: Partial<
  Record<ActivityType, number>
> = {
  [ActivityType.HeadToHead]: 2,
}

export const DEFAULT_MAX_PEOPLE: Partial<Record<ActivityType, number>> = {
  [ActivityType.HeadToHead]: 2,
}
